import { Button, Toolbar, ToolbarDivider, makeStyles, tokens } from '@fluentui/react-components';
import {
  TextBold20Regular,
  TextBulletListLtr20Regular,
  TextItalic20Regular,
  TextNumberListLtr20Regular,
  TextUnderline20Regular,
  Link20Regular,
} from '@fluentui/react-icons';
import { useEffect, useRef, type ReactElement } from 'react';

/** The editing commands, injected so tests do not depend on `document.execCommand`. */
export interface RichTextCommands {
  exec(command: string, value?: string): void;
}

export const documentCommands: RichTextCommands = {
  exec(command, value) {
    const target = document as Document & {
      execCommand?: (command: string, showUi: boolean, value?: string) => boolean;
    };
    target.execCommand?.(command, false, value);
  },
};

const useStyles = makeStyles({
  root: {
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
  },
  surface: {
    minHeight: '180px',
    maxHeight: '40vh',
    overflowY: 'auto',
    padding: '10px 12px',
    outline: 'none',
    background: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
  },
});

export interface RichTextEditorProps {
  readonly value: string;
  readonly onChange: (html: string) => void;
  readonly label: string;
  readonly commands?: RichTextCommands;
  readonly id?: string;
}

export function RichTextEditor(props: RichTextEditorProps): ReactElement {
  const styles = useStyles();
  const { value, onChange, label, commands = documentCommands, id } = props;
  const surface = useRef<HTMLDivElement | null>(null);

  // Only write into the DOM when it has drifted from `value`, so typing is not
  // interrupted by the caret being reset on every keystroke.
  useEffect(() => {
    const node = surface.current;
    if (node !== null && node.innerHTML !== value) node.innerHTML = value;
  }, [value]);

  const run = (command: string, commandValue?: string): void => {
    surface.current?.focus();
    commands.exec(command, commandValue);
    const node = surface.current;
    if (node !== null) onChange(node.innerHTML);
  };

  return (
    <div className={styles.root}>
      <Toolbar aria-label={`${label} formatting`} size="small">
        <Button
          appearance="subtle"
          icon={<TextBold20Regular />}
          aria-label="Bold"
          onClick={() => run('bold')}
        />
        <Button
          appearance="subtle"
          icon={<TextItalic20Regular />}
          aria-label="Italic"
          onClick={() => run('italic')}
        />
        <Button
          appearance="subtle"
          icon={<TextUnderline20Regular />}
          aria-label="Underline"
          onClick={() => run('underline')}
        />
        <ToolbarDivider />
        <Button
          appearance="subtle"
          icon={<TextBulletListLtr20Regular />}
          aria-label="Bulleted list"
          onClick={() => run('insertUnorderedList')}
        />
        <Button
          appearance="subtle"
          icon={<TextNumberListLtr20Regular />}
          aria-label="Numbered list"
          onClick={() => run('insertOrderedList')}
        />
        <ToolbarDivider />
        <Button
          appearance="subtle"
          icon={<Link20Regular />}
          aria-label="Insert link"
          onClick={() => {
            const url = window.prompt('Link address');
            if (url !== null && url !== '') run('createLink', url);
          }}
        />
      </Toolbar>
      <div
        {...(id === undefined ? {} : { id })}
        ref={surface}
        className={styles.surface}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={label}
        data-testid="compose-body"
        onInput={(event) => onChange(event.currentTarget.innerHTML)}
        onBlur={(event) => onChange(event.currentTarget.innerHTML)}
      />
    </div>
  );
}
